/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Stack, Box, Button, HStack, Tooltip, Popover, PopoverTrigger, PopoverContent,} from '@salesforce/retail-react-app/app/components/shared/ui'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {InfoCircle} from '../icons'
import Field from '../../components/field'
import useLoginFields from '../../components/forms/useLoginFields'
import {useIntl} from 'react-intl'

const LoginFields = ({
    form,
    handleForgotPasswordClick,
    prefix = '',
    hideEmail = false,
    hidePassword = false,
    isLoginPage = false
}) => {
    const fields = useLoginFields({form, prefix})
    const {formatMessage} = useIntl()
    return (
        <Stack spacing={5}>
            {!hideEmail && <Field {...fields.email} isLoginPage={isLoginPage} />}
            {!hidePassword && (
                <Stack gap={0}>
                    <Field {...fields.password} isLoginPage={isLoginPage} />
                    <HStack mt="16.5px">
                        <Field {...fields.rememberme} isLoginPage={isLoginPage} 
                            formControlProps={{
                            width: 'auto',
                            sx: {
                                '.chakra-checkbox__label': {
                                    lineHeight: '1'
                                },
                                '.chakra-checkbox__control': {
                                    marginTop: '-1px'
                                }
                            }
                            }}
                        />
                        <HideOnMobile
                            sx={{
                                '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                                    mt: '-1px'
                                }
                            }}
                        >
                            <Tooltip label={formatMessage({
                                defaultMessage: `Choosing “Keep me signed in” reduces the number of times you're asked to Sign-In on this device. To ensure the security of your account, use this option only on your personal devices.`,
                                id: 'use_login_fields.tooltip.rememberme'
                            })} 
                            bg="#ffffff" color={'inherit'} >
                                <InfoCircle  boxSize="18px" display="block"/>
                            </Tooltip>
                        </HideOnMobile>

                        <HideOnDesktop
                            sx={{
                                '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
                                    mt: '-1px'
                                }
                            }}
                        >
                            <Popover variant="small" >
                                <PopoverTrigger>
                                    <InfoCircle  boxSize="18px" display="block"/>
                                </PopoverTrigger>
                                <PopoverContent>
                                    {formatMessage({
                                        defaultMessage: `Choosing “Keep me signed in” reduces the number of times you're asked to Sign-In on this device. To ensure the security of your account, use this option only on your personal devices.`,
                                        id: 'use_login_fields.tooltip.rememberme'
                                    })}
                                </PopoverContent>
                            </Popover>
                        </HideOnDesktop>
                        {handleForgotPasswordClick && (
                            <Box ml="auto" textAlign="right">
                                <Button
                                    variant="link"
                                    onClick={handleForgotPasswordClick}
                                    fontSize="xs"
                                    lineHeight="15px"
                                    textDecoration="underline"
                                    >
                                    <FormattedMessage
                                        defaultMessage="Forgot password?"
                                        id="contact_info.link.forgot_password"
                                        />
                                </Button>
                            </Box>
                        )}
                    </HStack>
                </Stack>
            )}
        </Stack>
    )
}

LoginFields.propTypes = {
    handleForgotPasswordClick: PropTypes.func,

    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Optional configurations */
    hideEmail: PropTypes.bool,
    hidePassword: PropTypes.bool,

    isLoginPage: PropTypes.bool
}

export default LoginFields
